import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Check, X } from "lucide-react";

const ESTADO_BADGE = {
  pendiente:  { label: "Pendiente",  bg: "#fef3c7", color: "#92400e" },
  aprobado:   { label: "Aprobado",   bg: "#dcfce7", color: "#166534" },
  rechazado:  { label: "Rechazado",  bg: "#fee2e2", color: "#991b1b" },
};

function trimestreDeFecha(fecha) {
  if (!fecha) return "—";
  const mes = parseInt(fecha.split("-")[1], 10);
  if (mes <= 3)  return "T1";
  if (mes <= 6)  return "T2";
  if (mes <= 9)  return "T3";
  return "T4";
}

export default function RevisionIndicador() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [indicador, setIndicador]     = useState(null);
  const [actividades, setActividades] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState("");
  const [accionando, setAccionando]   = useState({});
  const [obsTexto, setObsTexto]       = useState({});
  // -1 = ninguno abierto. Solo 1 panel de rechazo abierto a la vez
  const [obsAbierta, setObsAbierta]   = useState(-1);
  const [user, setUser]               = useState(null);

  useEffect(() => {
    const u = JSON.parse(localStorage.getItem("auth_user") || "{}");
    setUser(u);

    const cargar = async () => {
      try {
        const resp = await fetch(
          `https://desarrollandoterritorios.com/api/pdd_indicador_revision.php?id=${id}`,
          { credentials: "include" }
        );
        const json = await resp.json();
        if (!json.ok) throw new Error(json.message || "Error cargando detalle");
        setIndicador(json.indicador);
        setActividades(json.actividades || []);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    cargar();
  }, [id]);

  const toggleObs = (actId) => {
    const numId = Number(actId);
    // Si está abierta esa, la cierra. Si no, abre solo esa.
    setObsAbierta((prev) => (prev === numId ? -1 : numId));
  };

  const ejecutarAccion = async (actId, accion) => {
    const numId = Number(actId);
    const obs   = obsTexto[numId] || "";
    if (accion === "rechazar" && !obs.trim()) {
      alert("Debe ingresar una observación para devolver el registro.");
      return;
    }

    setAccionando((p) => ({ ...p, [numId]: true }));
    try {
      const resp = await fetch(
        "https://desarrollandoterritorios.com/api/pdd_revisar_seguimiento.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            seguimiento_id: numId,
            accion,
            observacion: obs,
            revisado_por: user?.id || user?.user_id,
          }),
        }
      );
      const json = await resp.json();
      if (!json.ok) throw new Error(json.message);

      setActividades((prev) =>
        prev.map((a) =>
          Number(a.id) === numId
            ? { ...a, estado: json.estado, observacion_rechazo: accion === "rechazar" ? obs : null }
            : a
        )
      );
      setObsAbierta(-1);
      setObsTexto((p) => ({ ...p, [numId]: "" }));
    } catch (e) {
      alert(e.message || "Error al procesar");
    } finally {
      setAccionando((p) => ({ ...p, [numId]: false }));
    }
  };

  if (loading) return <div style={s.page}><div style={s.msg}>Cargando detalle...</div></div>;
  if (error)   return <div style={s.page}><div style={s.msgError}>Error: {error}</div></div>;

  const porTrimestre = { T1: [], T2: [], T3: [], T4: [] };
  actividades.forEach((a) => {
    const t = trimestreDeFecha(a.fecha);
    if (porTrimestre[t]) porTrimestre[t].push(a);
  });

  return (
    <div style={s.page}>
      <div style={s.wrapper}>

        <button onClick={() => navigate(-1)} style={s.backBtn}>← Regresar</button>

        <div style={s.infoBox}>
          <div style={s.infoRow}><span style={s.infoLabel}>Código</span><span style={s.infoVal}>{indicador?.numero_indicador}</span></div>
          <div style={s.infoRow}><span style={s.infoLabel}>Nombre</span><span style={s.infoVal}>{indicador?.nombre_completo}</span></div>
          <div style={s.infoRow}><span style={s.infoLabel}>Unidad</span><span style={s.infoVal}>{indicador?.unidad_medida}</span></div>
          <div style={s.infoRow}><span style={s.infoLabel}>Meta cuatrienio</span><span style={s.infoVal}>{indicador?.meta_cuatrenio}</span></div>
          <div style={s.infoRow}><span style={s.infoLabel}>Dependencia</span><span style={s.infoVal}>{indicador?.dependencia}</span></div>
        </div>

        {actividades.length === 0 && (
          <div style={s.emptyCard}>No hay actividades registradas para este indicador.</div>
        )}

        {["T1", "T2", "T3", "T4"].map((t) => {
          const items = porTrimestre[t];
          if (items.length === 0) return null;
          return (
            <div key={t} style={s.card}>
              <div style={s.cardHeader}>
                <span style={s.cardTitle}>{t} — {items.length} actividad{items.length !== 1 ? "es" : ""}</span>
              </div>

              {items.map((act) => {
                const numId   = Number(act.id);
                const badge   = ESTADO_BADGE[act.estado] || ESTADO_BADGE.pendiente;
                const cargando = accionando[numId];
                const showObs  = obsAbierta === numId;

                return (
                  <div key={act.id} style={s.actCard}>
                    <div style={s.actTop}>
                      <div style={s.actInfo}>
                        <div style={s.grid4}>
                          <div style={s.campo}>
                            <span style={s.campoLabel}>Fecha</span>
                            <span style={s.campoVal}>{act.fecha}</span>
                          </div>
                          <div style={s.campo}>
                            <span style={s.campoLabel}>Cantidad realizada</span>
                            <span style={s.campoVal}>{act.cantidad_realizada}</span>
                          </div>
                          <div style={s.campo}>
                            <span style={s.campoLabel}>Tipo de evidencia</span>
                            <span style={s.campoVal}>{act.tipo_evidencia || "—"}</span>
                          </div>
                          <div style={s.campo}>
                            <span style={s.campoLabel}>Registrado por</span>
                            <span style={s.campoVal}>{act.nombre_usuario || `Usuario ${act.usuario_id}`}</span>
                          </div>
                        </div>

                        <div style={s.campo}>
                          <span style={s.campoLabel}>Observación / actividad</span>
                          <span style={s.campoVal}>{act.actividad}</span>
                        </div>

                        {act.ubicacion_evidencia && (
                          <div style={s.campo}>
                            <span style={s.campoLabel}>Ubicación evidencia</span>
                            <a href={act.ubicacion_evidencia} target="_blank" rel="noreferrer" style={s.link}>
                              {act.ubicacion_evidencia}
                            </a>
                          </div>
                        )}

                        {act.estado === "rechazado" && act.observacion_rechazo && (
                          <div style={s.obsRechazo}>
                            Motivo de devolución: {act.observacion_rechazo}
                          </div>
                        )}
                      </div>

                      <div style={s.actAcciones}>
                        <span style={{ ...s.badge, background: badge.bg, color: badge.color }}>
                          {badge.label}
                        </span>
                        {act.estado !== "aprobado" && (
                          <>
                            <button style={s.btnAprobar} disabled={cargando}
                              onClick={() => ejecutarAccion(numId, "aprobar")}>
                              <Check size={14} /> Aprobar
                            </button>
                            <button style={s.btnDevolver} disabled={cargando}
                              onClick={() => toggleObs(numId)}>
                              <X size={14} /> {showObs ? "Cancelar" : "Devolver"}
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    {showObs && (
                      <div style={s.obsPanel}>
                        <textarea rows={3} style={s.obsInput}
                          placeholder="Describe qué debe corregir la secretaría..."
                          value={obsTexto[numId] || ""}
                          onChange={(e) =>
                            setObsTexto((p) => ({ ...p, [numId]: e.target.value }))
                          }
                        />
                        <div style={{ display:"flex", gap:8, marginTop:8 }}>
                          <button style={s.btnConfirmar} disabled={cargando}
                            onClick={() => ejecutarAccion(numId, "rechazar")}>
                            {cargando ? "Enviando..." : "Confirmar devolución"}
                          </button>
                          <button style={s.btnCancelar} onClick={() => setObsAbierta(-1)}>
                            Cancelar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const s = {
  page:         { minHeight:"100vh", background:"#f5f7fb", padding:"24px" },
  wrapper:      { maxWidth:"1100px", margin:"0 auto", display:"flex", flexDirection:"column", gap:16 },
  backBtn:      { border:"none", borderRadius:10, background:"#1f3b64", color:"#fff", padding:"10px 16px",
    cursor:"pointer", fontWeight:700, fontSize:14, alignSelf:"flex-start" },
  msg:          { textAlign:"center", padding:40, color:"#64748b" },
  msgError:     { textAlign:"center", padding:40, color:"#991b1b" },
  infoBox:      { background:"#fff", borderRadius:14, border:"1px solid #e2e8f0", padding:"16px 20px",
    display:"flex", flexDirection:"column", gap:8 },
  infoRow:      { display:"flex", gap:12, fontSize:14 },
  infoLabel:    { color:"#64748b", minWidth:140, fontWeight:600 },
  infoVal:      { color:"#1e293b" },
  emptyCard:    { background:"#fff", borderRadius:14, border:"1px solid #e2e8f0", padding:"24px",
    textAlign:"center", color:"#64748b", fontSize:14 },
  card:         { background:"#fff", borderRadius:14, border:"1px solid #e2e8f0", overflow:"hidden" },
  cardHeader:   { padding:"12px 20px", background:"#1677c8" },
  cardTitle:    { color:"#fff", fontWeight:700, fontSize:15 },
  actCard:      { borderBottom:"1px solid #f1f5f9", padding:"14px 20px" },
  actTop:       { display:"flex", gap:16, justifyContent:"space-between", flexWrap:"wrap" },
  actInfo:      { flex:1, display:"flex", flexDirection:"column", gap:10 },
  grid4:        { display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(180px, 1fr))", gap:10 },
  campo:        { display:"flex", flexDirection:"column", gap:2 },
  campoLabel:   { fontSize:11, color:"#94a3b8", fontWeight:600, textTransform:"uppercase" },
  campoVal:     { fontSize:14, color:"#1e293b" },
  link:         { fontSize:13, color:"#1677c8", wordBreak:"break-all" },
  obsRechazo:   { fontSize:13, color:"#991b1b", background:"#fee2e2", borderRadius:8, padding:"8px 12px" },
  actAcciones:  { display:"flex", flexDirection:"column", alignItems:"flex-end", gap:8, flexShrink:0 },
  badge:        { fontSize:12, padding:"4px 10px", borderRadius:999, fontWeight:600, whiteSpace:"nowrap" },
  btnAprobar:   { display:"flex", alignItems:"center", gap:4, border:"none", borderRadius:8,
    background:"#16a34a", color:"#fff", padding:"8px 14px", cursor:"pointer", fontWeight:700, fontSize:13 },
  btnDevolver:  { display:"flex", alignItems:"center", gap:4, border:"none", borderRadius:8,
    background:"#ef4444", color:"#fff", padding:"8px 14px", cursor:"pointer", fontWeight:700, fontSize:13 },
  obsPanel:     { marginTop:12, borderTop:"1px solid #e2e8f0", paddingTop:12 },
  obsInput:     { width:"100%", borderRadius:8, border:"1px solid #cbd5e1", padding:"10px 12px",
    fontSize:13, resize:"vertical", boxSizing:"border-box", outline:"none" },
  btnConfirmar: { border:"none", borderRadius:8, background:"#dc2626", color:"#fff",
    padding:"9px 16px", cursor:"pointer", fontWeight:700, fontSize:13 },
  btnCancelar:  { border:"1px solid #cbd5e1", borderRadius:8, background:"#fff",
    color:"#475569", padding:"9px 16px", cursor:"pointer", fontSize:13 },
};